import React, { useState, useCallback, useRef, useEffect } from 'react';
import TerminalInput from './TerminalInput';
import { matchAnswer } from '../engine/answerMatcher';
import DomainBadge from '../../../shared/components/DomainBadge';
import useTimer from '../../../shared/hooks/useTimer';

/**
 * Speed Round mode: rapid-fire single-command questions with 30-second timer.
 */
export default function SpeedRound({ questions, onComplete, onUpdateStats }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [streak, setStreak] = useState(0);

  const timer = useTimer({
    initialSeconds: 30,
    mode: 'countdown',
    onExpire: () => handleTimeout(),
    warningThresholds: [10, 5],
  });

  const currentQ = questions[currentIndex];

  // Start timer for each question
  useEffect(() => {
    if (currentQ && !showFeedback) {
      timer.reset(30);
      timer.start();
    }
    return () => timer.pause();
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback((input) => {
    if (!currentQ || showFeedback) return;
    timer.pause();

    const result = matchAnswer(input, currentQ.accepted_answers, currentQ.common_mistakes || []);
    const timeTaken = 30 - timer.seconds;
    const isCorrect = result.percentage === 100;

    setLastResult({ ...result, timeTaken, isCorrect });
    setResults(prev => [...prev, {
      questionId: currentQ.id,
      percentage: result.percentage,
      timeTaken,
      isCorrect,
    }]);

    setStreak(prev => isCorrect ? prev + 1 : 0);
    onUpdateStats?.(currentQ.id, isCorrect, timeTaken);
    setShowFeedback(true);

    // Auto-advance after 2 seconds
    setTimeout(() => {
      setShowFeedback(false);
      setLastResult(null);
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        onComplete?.(results);
      }
    }, 2000);
  }, [currentQ, showFeedback, timer, currentIndex, questions.length, results, onComplete, onUpdateStats]);

  const handleTimeout = useCallback(() => {
    if (showFeedback) return;
    const primaryAnswer = currentQ?.accepted_answers?.[0];

    setLastResult({
      percentage: 0,
      feedback: 'Time\'s up!',
      lineResults: primaryAnswer ? primaryAnswer.commands.map(cmd => ({
        expected: cmd, userLine: null, score: 0, similarity: 0,
      })) : [],
      matchedMistakes: [],
      timeTaken: 30,
      isCorrect: false,
    });

    setStreak(0);
    setResults(prev => [...prev, {
      questionId: currentQ?.id,
      percentage: 0,
      timeTaken: 30,
      isCorrect: false,
    }]);
    setShowFeedback(true);

    setTimeout(() => {
      setShowFeedback(false);
      setLastResult(null);
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        onComplete?.(results);
      }
    }, 2000);
  }, [currentQ, showFeedback, currentIndex, questions.length, results, onComplete]);

  if (!currentQ) return null;

  const timerColor = timer.seconds > 10 ? '#00ff41' : timer.seconds > 5 ? '#F39C12' : '#E74C3C';
  const primaryAnswer = currentQ.accepted_answers.find(a => a.is_primary) || currentQ.accepted_answers[0];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Top bar: timer, progress, streak */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-mono" style={{ color: '#00cc33' }}>
          {currentIndex + 1}/{questions.length}
        </span>
        <span className="text-3xl font-bold font-mono" style={{ color: timerColor }}>
          {timer.formatted}
        </span>
        {streak > 1 && (
          <span className="text-sm text-yellow-500 font-mono">🔥 {streak}</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-800 rounded-full h-1 mb-6">
        <div
          className="h-1 rounded-full transition-all"
          style={{ width: `${(currentIndex / questions.length) * 100}%`, backgroundColor: '#00ff41' }}
        />
      </div>

      {/* Question */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <DomainBadge domainId={currentQ.domain} size="xs" />
          <span className="text-xs text-gray-500 font-mono">{currentQ.category}</span>
        </div>
        <p className="text-white text-lg">{currentQ.prompt}</p>
      </div>

      {/* Input or feedback */}
      {!showFeedback ? (
        <TerminalInput
          prompt={currentQ.context_mode || 'Router#'}
          onSubmit={handleSubmit}
          multiline={false}
        />
      ) : (
        <div className={`rounded-lg p-4 text-center ${
          lastResult?.isCorrect ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
        }`}>
          <div className="text-2xl mb-2">
            {lastResult?.isCorrect ? '✓' : '✗'}
          </div>
          <p className={`font-mono text-sm ${lastResult?.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
            {lastResult?.isCorrect ? `Correct! (${lastResult.timeTaken.toFixed(1)}s)` : 'Incorrect'}
          </p>
          {!lastResult?.isCorrect && primaryAnswer && (
            <p className="text-gray-400 text-sm font-mono mt-2">
              Answer: {primaryAnswer.commands.join(' | ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
